import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaCheckCircle, FaWeight, FaRuler, FaCamera, FaClock } from 'react-icons/fa';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import TemplateField from '@/components/TemplateFieldRenderer';

const API = process.env.REACT_APP_BACKEND_URL;

// IDs de preguntas ya hardcodeadas en este formulario. Si el admin agrega un
// campo al template con uno de estos IDs, no se vuelve a renderizar al
// recorrer `formTemplate.fields` (ticket #265569 — evita duplicados).
const HARDCODED_FIELD_IDS = new Set([
  'weight', 'body_measurements', 'adherence_level', 'meal_feeling', 'digestion', 'inflammation',
  'training_time_same', 'new_training_time', 'meals_count', 'post_workout_meal_same', 'new_post_workout_meal',
  'current_training_location', 'objective_changed', 'new_objective', 'preferences_changed', 'preferences_notes',
  'uses_supplements', 'available_equipment', 'new_pathologies', 'new_allergies', 'notes',
  'training_volume_feedback', 'exercises_to_remove', 'new_equipment_available', 'muscle_group_priority',
]);

/**
 * Formulario de actualización Fit Evolution cada 4 semanas.
 * Anual: se muestra cada 30 días. Mensual: 4 días antes de vencer.
 */
export default function FitEvolutionUpdate({ user }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    weight: '',
    waist: '', hip: '', chest: '', arm: '', thigh: '',
    adherence_level: '',
    // Nuevas preguntas de bienestar (lógica de ajuste)
    digestion: '',           // bien | lenta | gases | estreñimiento
    inflammation: '',        // no | a_veces | frecuente
    meal_feeling: '',        // bien | pesada | con_hambre | sin_apetito
    training_time_same: '',  // si | no
    new_training_time: '',
    post_workout_meal_same: '', // si | no
    new_post_workout_meal: '',
    // Objetivo con dropdown
    objective_changed: false,
    new_objective: '',       // perder_grasa | aumentar_masa | recomposicion | mantener | salud
    preferences_changed: false,
    preferences_notes: '',
    new_pathologies: '',
    new_allergies: '',
    // 🏠 Implementos (solo si entrena en casa)
    equipment_changed: false,
    available_equipment: [],
    // ═══ Extras al completar 12 semanas (ciclo nuevo de rutina) ═══
    training_volume_feedback: '',      // muy_alto | perfecto | muy_bajo
    exercises_to_remove: '',
    new_equipment_available: '',
    current_training_location: '',     // casa | gimnasio | ambos (SIEMPRE visible)
    muscle_group_priority: '',
    // Cadencia mensual: cuántas comidas y cuál es la post-entreno
    meals_count: 5,
    // 2026-02 · Suplementación (misma pregunta que en el form inicial). null = sin responder.
    uses_supplements: null,
    notes: ''
  });
  const [equipmentCatalog, setEquipmentCatalog] = useState([]);
  // Ticket #265569 · Respuestas a preguntas agregadas por el admin, keyed por field.id.
  const [customAnswers, setCustomAnswers] = useState({});
  const setCustomAnswer = (fieldId, val) => setCustomAnswers(prev => ({ ...prev, [fieldId]: val }));
  const [welcomeBack, setWelcomeBack] = useState(null); // {needs_welcome_back, weeks_inactive}
  const [wbWasTraining, setWbWasTraining] = useState('');
  const [wbWasDieting, setWbWasDieting] = useState('');
  // 2026-02 · Template dinámico desde `form_templates` (form_type=fit_evolution_update).
  const [formTemplate, setFormTemplate] = useState(null);
  const tplField = (id) => (formTemplate?.fields || []).find(f => f.id === id) || null;
  const tplLabel = (id, fallback) => tplField(id)?.label || fallback;
  const tplDesc = (id, fallback = '') => tplField(id)?.description || fallback;
  const tplOptions = (id, fallback) => {
    const f = tplField(id);
    return Array.isArray(f?.options) && f.options.length > 0 ? f.options : fallback;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('deisycfit_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(
          `${API}/api/form-templates/active?form_type=fit_evolution_update&plan=basic`,
          { headers, withCredentials: false }
        );
        if (!cancelled && res.data?.template) setFormTemplate(res.data.template);
      } catch (e) { /* silencio: usamos fallback hardcodeado */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let ignore = false;
    axios.get(`${API}/api/fit-evolution/equipment-catalog`)
      .then(r => { if (!ignore) setEquipmentCatalog(r.data?.catalog || []); })
      .catch(() => {});
    return () => { ignore = true; };
  }, []);

  const toggleEquipment = (id) => {
    setForm(prev => {
      const has = (prev.available_equipment || []).includes(id);
      return {
        ...prev,
        available_equipment: has
          ? prev.available_equipment.filter(x => x !== id)
          : [...(prev.available_equipment || []), id],
      };
    });
  };

  useEffect(() => {
    (async () => {
      if (!user?.email) { setLoading(false); return; }
      try {
        const res = await axios.get(`${API}/api/personalized-plans/fit-evolution/update-check/${encodeURIComponent(user.email)}`);
        setStatus(res.data);
      } catch (e) { console.error(e); }
      // Chequear si el usuario vuelve tras >4 semanas inactivo (welcome-back).
      try {
        const wb = await axios.get(`${API}/api/personalized-plans/fit-evolution/welcome-back-check/${encodeURIComponent(user.email)}`);
        setWelcomeBack(wb.data);
      } catch (e) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [user]);

  const handleSubmit = async () => {
    if (!form.weight) {
      toast.error('El peso actual es obligatorio');
      return;
    }
    // Validación welcome-back: si aplica, ambas respuestas son obligatorias.
    if (welcomeBack?.needs_welcome_back && (wbWasTraining === '' || wbWasDieting === '')) {
      toast.error('Contanos cómo te fue estas semanas antes de continuar');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        email: user.email,
        weight: parseFloat(form.weight) || null,
        body_measurements: {
          waist: parseFloat(form.waist) || null,
          hip: parseFloat(form.hip) || null,
          chest: parseFloat(form.chest) || null,
          arm: parseFloat(form.arm) || null,
          thigh: parseFloat(form.thigh) || null
        },
        adherence_level: form.adherence_level,
        digestion: form.digestion || null,
        inflammation: form.inflammation || null,
        meal_feeling: form.meal_feeling || null,
        training_time_same: form.training_time_same === 'si',
        new_training_time: form.training_time_same === 'no' ? form.new_training_time : null,
        post_workout_meal_same: form.post_workout_meal_same === 'si',
        new_post_workout_meal: form.post_workout_meal_same === 'no' ? form.new_post_workout_meal : null,
        objective_changed: !!form.objective_changed,
        new_objective: form.new_objective || null,
        preferences_changed: !!form.preferences_changed,
        preferences_notes: form.preferences_notes || null,
        new_pathologies: form.new_pathologies ? form.new_pathologies.split(',').map(s => s.trim()).filter(Boolean) : [],
        new_allergies: form.new_allergies ? form.new_allergies.split(',').map(s => s.trim()).filter(Boolean) : [],
        equipment_changed: true,
        available_equipment: (form.available_equipment || []),
        // Preferencias del ciclo (siempre visibles ahora)
        meals_count: parseInt(form.meals_count) || 5,
        current_training_location: form.current_training_location || null,
        // Extras 12 semanas (solo si el status lo indica)
        training_volume_feedback: status?.is_12week_cycle ? (form.training_volume_feedback || null) : null,
        exercises_to_remove: status?.is_12week_cycle ? (form.exercises_to_remove || null) : null,
        new_equipment_available: status?.is_12week_cycle ? (form.new_equipment_available || null) : null,
        muscle_group_priority: status?.is_12week_cycle ? (form.muscle_group_priority || null) : null,
        notes: form.notes || null,
        // Welcome-back (>4 semanas inactivo): sólo si el check lo activó.
        wb_was_training: welcomeBack?.needs_welcome_back ? (wbWasTraining === 'si') : null,
        wb_was_dieting: welcomeBack?.needs_welcome_back ? (wbWasDieting === 'si') : null,
        // 2026-02 · Suplementación. Sólo se envía si la usuaria respondió (no sobrescribe con false por defecto).
        uses_supplements: form.uses_supplements === null ? null : !!form.uses_supplements,
        // Ticket #265569 · Respuestas a preguntas agregadas por el admin.
        custom_answers: customAnswers,
      };
      const res = await axios.post(`${API}/api/personalized-plans/fit-evolution/submit-update`, payload);
      if (res.data?.success) {
        // El mensaje "En 24 horas..." solo aplica cuando el plan renovado
        // todavía no fue entregado (pending_release y next_release_at en el
        // futuro). Si el backend ya liberó el plan (o no hay pendiente),
        // mostramos un mensaje neutral sin la promesa de 24h.
        let showPending24h = false;
        try {
          const ready = await axios.get(
            `${API}/api/personalized-plans/fit-evolution/plan-ready-status/${encodeURIComponent(user.email)}`
          );
          const stillPending = !!ready.data?.update_pending_release;
          showPending24h = stillPending;
        } catch { /* silent: si falla, no mostramos la promesa de 24h */ }

        toast.success(
          showPending24h
            ? '✅ Actualización enviada. Tu coach liberará tu plan renovado; mientras tanto sigues viendo el actual.'
            : '✅ Actualización enviada. Tu plan ya está listo para verlo.',
          { duration: 8000 }
        );
        setSubmitted(true);
      } else {
        toast.error(res.data?.detail || 'No se pudo enviar');
      }
    } catch (e) {
      if (e.response?.status === 409) {
        // Ya se había enviado este ciclo (doble click / doble tab) — no es
        // un error real, solo mostramos la pantalla de "ya enviado".
        toast.message(e.response?.data?.detail || 'Ya habías enviado tu actualización de este ciclo.');
        setSubmitted(true);
      } else {
        toast.error(e.response?.data?.detail || 'Error al enviar');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}><div className="animate-spin w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full" /></div>;

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#0a0a0a' }} data-testid="fit-evo-just-submitted">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md text-center rounded-3xl p-8" style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.15), rgba(16,185,129,0.05))', border: '1px solid rgba(34,197,94,0.4)' }}>
        <FaCheckCircle className="text-6xl text-green-400 mx-auto mb-4" />
        <h2 className="text-2xl font-black text-white mb-3">¡Actualización enviada!</h2>
        <p className="text-white/80 mb-6">Tu coach está preparando la nueva versión de tu plan. Estará lista muy pronto.</p>
        <Button onClick={() => navigate('/dashboard')} className="rounded-full font-bold text-white px-8 py-6" style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}>Volver al Dashboard</Button>
      </motion.div>
    </div>
  );

  // Ticket #265569 · Si el status ya cargó y no hay actualización pendiente,
  // no mostramos el formulario — evita que se pueda volver a enviar
  // recargando la página. Hay DOS razones distintas para `has_pending_update
  // === false` y el texto/encabezado debe dejar clara cuál aplica, para que
  // una clienta nueva (ventana aún no abierta) no lea un mensaje pensado
  // para "ya la enviaste":
  //   - `already_filled_this_window`: ya la llenó, debe esperar al próximo ciclo.
  //   - `days_until_next_form` presente: todavía no le toca (ventana no abrió).
  //   - ningún otro dato (reason=user_not_found/not_fit_evolution): este
  //     formulario no aplica a su cuenta.
  if (status && status.has_pending_update === false) {
    const alreadyFilled = status.already_filled_this_window === true;
    const notYetDue = !alreadyFilled && typeof status.days_until_next_form === 'number';
    const heading = alreadyFilled ? '¡Ya la enviaste! 🎉' : notYetDue ? 'Todavía no te toca' : 'No disponible';
    const fallbackMessage = notYetDue
      ? 'Tu próximo formulario de actualización aún no está disponible.'
      : 'Este formulario no está disponible en este momento.';
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#0a0a0a' }} data-testid="fit-evo-already-submitted">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md text-center rounded-3xl p-8" style={{ background: 'linear-gradient(135deg, rgba(0,240,255,0.12), rgba(0,240,255,0.03))', border: '1px solid rgba(0,240,255,0.35)' }}>
          {notYetDue ? <FaClock className="text-6xl text-cyan-300 mx-auto mb-4" /> : <FaCheckCircle className="text-6xl text-cyan-300 mx-auto mb-4" />}
          <h2 className="text-2xl font-black text-white mb-3">{heading}</h2>
          <p className="text-white/80 mb-6">{status.message || fallbackMessage}</p>
          <Button onClick={() => navigate('/dashboard')} className="rounded-full font-bold text-white px-8 py-6" style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}>Volver al Dashboard</Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 md:px-6" style={{ backgroundColor: '#0a0a0a' }} data-testid="fit-evo-update">
      <div className="fixed inset-0 z-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 15% 85%, rgba(var(--brand-primary-glow),0.14), transparent 55%), radial-gradient(circle at 85% 15%, rgba(0,240,255,0.10), transparent 45%)' }} />
      <div className="max-w-2xl mx-auto relative z-10">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white mb-6 hover:bg-white/10"><FaArrowLeft className="mr-2" /> Volver</Button>

        <div className="text-center mb-8">
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] mb-2" style={{ color: 'var(--brand-primary)' }}>Fit Evolution · Actualización de plan</p>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {status?.weeks_completed
              ? `¡Ya vamos a cumplir ${status.weeks_completed} semanas! 🎉`
              : 'Cuéntanos tu evolución'}
          </h1>
          <p className="text-gray-300 text-sm md:text-base max-w-lg mx-auto">
            {status?.weeks_completed
              ? 'Responde estas preguntas para que ajustemos tu próximo mes según tu progreso real.'
              : 'Cada 4 semanas ajustamos tu plan a tu progreso real. Responde con lo que sientes ahora.'}
          </p>
          {status?.message && <p className="text-cyan-300/90 text-sm mt-3 italic">{status.message}</p>}
        </div>

        <div className="rounded-3xl p-6 md:p-8 space-y-6" style={{ background: 'rgba(26,26,26,0.9)', border: '1px solid rgba(255,255,255,0.1)' }}>

          {/* ═══ WELCOME-BACK: preguntas extra si el usuario lleva >4 semanas inactivo ═══ */}
          {welcomeBack?.needs_welcome_back && (
            <div
              className="rounded-2xl p-4 mb-2"
              style={{
                background: 'linear-gradient(135deg, rgba(250,204,21,0.14), rgba(236,72,153,0.08))',
                border: '1px solid rgba(250,204,21,0.45)',
              }}
              data-testid="welcome-back-block"
            >
              <p className="text-[11px] uppercase tracking-widest text-yellow-300/95 font-bold mb-1">
                👋 ¡Qué bueno verte de nuevo!
              </p>
              <p className="text-sm text-white/90 mb-3 leading-snug">
                Llevas <strong>{welcomeBack.weeks_inactive} semanas</strong> sin llenar tu formulario. Antes de continuar, contanos cómo te fue este tiempo — así ajustamos tu programación al momento actual.
              </p>
              <div className="space-y-3">
                <div>
                  <Label className="text-white/85 mb-1 block text-sm">
                    ¿Estuviste entrenando en este tiempo?
                  </Label>
                  <select
                    value={wbWasTraining}
                    onChange={(e) => setWbWasTraining(e.target.value)}
                    className="w-full bg-black/40 border border-yellow-400/40 text-white rounded-md px-3 py-2 text-sm"
                    data-testid="wb-was-training"
                  >
                    <option value="">— Selecciona —</option>
                    <option value="si">Sí, seguí entrenando</option>
                    <option value="no">No, no entrené</option>
                  </select>
                </div>
                <div>
                  <Label className="text-white/85 mb-1 block text-sm">
                    ¿Seguiste algún plan nutricional?
                  </Label>
                  <select
                    value={wbWasDieting}
                    onChange={(e) => setWbWasDieting(e.target.value)}
                    className="w-full bg-black/40 border border-yellow-400/40 text-white rounded-md px-3 py-2 text-sm"
                    data-testid="wb-was-dieting"
                  >
                    <option value="">— Selecciona —</option>
                    <option value="si">Sí, cuidé mi alimentación</option>
                    <option value="no">No, no seguí ningún plan</option>
                  </select>
                </div>
                {wbWasTraining === 'no' && wbWasDieting === 'no' && (
                  <p className="text-[12px] text-yellow-200/90 italic leading-snug">
                    ⚠️ Vamos a reiniciar tu programa desde la semana 1 para acompañarte con una adaptación segura.
                  </p>
                )}
                {(wbWasTraining === 'si' || wbWasDieting === 'si') && (wbWasTraining && wbWasDieting) && (
                  <p className="text-[12px] text-green-300/90 italic leading-snug">
                    ✨ Vamos a mantener tu nivel actual y armar un plan nuevo completo desde donde estabas.
                  </p>
                )}
              </div>
            </div>
          )}

          <SectionTitle icon={<FaWeight />} label="Peso actual" />
          <div>
            <Label className="text-white/80 mb-1 block">{tplLabel('weight', 'Peso (kg)')} *</Label>
            <Input type="number" step="0.1" value={form.weight} onChange={(e) => setForm({...form, weight: e.target.value})} placeholder={tplField('weight')?.placeholder || 'Ej: 62.5'} className="bg-black/40 border-white/15 text-white" data-testid="update-weight" />
          </div>

          <SectionTitle icon={<FaRuler />} label={tplLabel('body_measurements', 'Medidas corporales (opcional)')} />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {(tplOptions('body_measurements', [
              {value:'waist', label:'Cintura'}, {value:'hip', label:'Cadera'}, {value:'chest', label:'Pecho'},
              {value:'arm', label:'Brazo'}, {value:'thigh', label:'Muslo'}
            ])).map(m => (
              <div key={m.value || m.k}>
                <Label className="text-white/70 text-xs mb-1 block">{m.label || m.l} (cm)</Label>
                <Input type="number" step="0.5" value={form[m.value || m.k]} onChange={(e) => setForm({...form, [m.value || m.k]: e.target.value})} className="bg-black/40 border-white/15 text-white text-sm" />
              </div>
            ))}
          </div>

          <SectionTitle icon={<FaCamera />} label={tplLabel('adherence_level', 'Nivel de adherencia al plan')} />
          <div className="grid grid-cols-3 gap-2">
            {(tplOptions('adherence_level', [
              {value:'alta', label:'💯 Alta', description:'Cumplí casi todo'},
              {value:'media', label:'🙂 Media', description:'Cumplí la mayoría'},
              {value:'baja', label:'😅 Baja', description:'Me costó bastante'}
            ])).map(o => (
              <button key={o.value || o.v} type="button" onClick={() => setForm({...form, adherence_level: o.value || o.v})}
                className={`text-left p-3 rounded-lg border transition-all ${form.adherence_level === (o.value || o.v) ? 'bg-pink-500/20 border-pink-400' : 'bg-black/40 border-white/15 hover:border-white/30'}`}
                data-testid={`adherence-${o.value || o.v}`}>
                <p className="font-bold text-white text-sm">{o.label || o.l}</p>
                <p className="text-[11px] text-white/70">{o.description || o.d}</p>
              </button>
            ))}
          </div>

          <SectionTitle icon={<FaCamera />} label="Bienestar y digestión" />
          <div>
            <Label className="text-white/80 mb-1 block text-sm">{tplLabel('meal_feeling', '¿Cómo te has sentido con las comidas?')}</Label>
            <select value={form.meal_feeling} onChange={(e) => setForm({...form, meal_feeling: e.target.value})}
              className="w-full bg-black/40 border border-white/15 text-white rounded-md px-3 py-2 text-sm" data-testid="meal-feeling">
              <option value="">— Selecciona —</option>
              {(tplOptions('meal_feeling', [
                {value:'bien', label:'😊 Bien, satisfecha'},
                {value:'pesada', label:'😪 Pesada, muy llena'},
                {value:'con_hambre', label:'😤 Con hambre entre comidas'},
                {value:'sin_apetito', label:'😐 Sin apetito'},
              ])).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-white/80 mb-1 block text-sm">{tplLabel('digestion', '¿Cómo está tu digestión?')}</Label>
            <select value={form.digestion} onChange={(e) => setForm({...form, digestion: e.target.value})}
              className="w-full bg-black/40 border border-white/15 text-white rounded-md px-3 py-2 text-sm" data-testid="digestion">
              <option value="">— Selecciona —</option>
              {(tplOptions('digestion', [
                {value:'bien', label:'✨ Bien, sin problemas'},
                {value:'lenta', label:'🐢 Lenta / estreñimiento'},
                {value:'gases', label:'💨 Con gases'},
                {value:'acidez', label:'🔥 Acidez / reflujo'},
              ])).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-white/80 mb-1 block text-sm">{tplLabel('inflammation', '¿Te has sentido inflamada?')}</Label>
            <select value={form.inflammation} onChange={(e) => setForm({...form, inflammation: e.target.value})}
              className="w-full bg-black/40 border border-white/15 text-white rounded-md px-3 py-2 text-sm" data-testid="inflammation">
              <option value="">— Selecciona —</option>
              {(tplOptions('inflammation', [
                {value:'no', label:'👍 No, muy bien'},
                {value:'a_veces', label:'🤔 A veces'},
                {value:'frecuente', label:'😖 Frecuentemente'},
              ])).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <SectionTitle icon={<FaCamera />} label="Entrenamiento" />
          <div>
            <Label className="text-white/80 mb-1 block text-sm">{tplLabel('training_time_same', '¿Sigues entrenando en el mismo horario?')}</Label>
            <select value={form.training_time_same} onChange={(e) => setForm({...form, training_time_same: e.target.value})}
              className="w-full bg-black/40 border border-white/15 text-white rounded-md px-3 py-2 text-sm" data-testid="training-time-same">
              <option value="">— Selecciona —</option>
              {(tplOptions('training_time_same', [
                {value:'si', label:'Sí, el mismo'},
                {value:'no', label:'No, cambió'},
              ])).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {form.training_time_same === 'no' && (
              <Input value={form.new_training_time} onChange={(e) => setForm({...form, new_training_time: e.target.value})}
                placeholder="Nuevo horario (ej: 6am, 7pm)" className="bg-black/40 border-white/15 text-white mt-2" />
            )}
          </div>
          <div>
            <Label className="text-white/80 mb-1 block text-sm">
              {tplLabel('meals_count', '¿Cuántas comidas quieres llevar en tu plan este mes?')}
            </Label>
            <select
              value={form.meals_count || 5}
              onChange={(e) => setForm({...form, meals_count: parseInt(e.target.value) || 5})}
              className="w-full bg-black/40 border border-white/15 text-white rounded-md px-3 py-2 text-sm"
              data-testid="meals-count"
            >
              {(tplOptions('meals_count', [
                {value:'3', label:'3 comidas'},
                {value:'4', label:'4 comidas'},
                {value:'5', label:'5 comidas'},
                {value:'6', label:'6 comidas'},
              ])).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-white/80 mb-1 block text-sm">
              {tplLabel('post_workout_meal_same', '¿Tu comida post entrenamiento sigue siendo la misma?')}
              <span className="text-white/60 font-normal"> ({tplDesc('post_workout_meal_same', 'la comida después de entrenar')})</span>
            </Label>
            <select value={form.post_workout_meal_same} onChange={(e) => setForm({...form, post_workout_meal_same: e.target.value})}
              className="w-full bg-black/40 border border-white/15 text-white rounded-md px-3 py-2 text-sm" data-testid="post-workout-same">
              <option value="">— Selecciona —</option>
              {(tplOptions('post_workout_meal_same', [
                {value:'si', label:'Sí, la misma'},
                {value:'no', label:'No, ahora es otra'},
              ])).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {form.post_workout_meal_same === 'no' && (
              <select value={form.new_post_workout_meal} onChange={(e) => setForm({...form, new_post_workout_meal: e.target.value})}
                className="w-full bg-black/40 border border-white/15 text-white rounded-md px-3 py-2 text-sm mt-2"
                data-testid="new-post-workout-meal">
                <option value="">— Elige tu comida post entreno —</option>
                {Array.from({ length: form.meals_count || 5 }, (_, i) => i + 1).map(n => (
                  <option key={n} value={`comida_${n}`}>Comida {n}</option>
                ))}
                <option value="ninguna">Ninguna (entreno cerca de dormir)</option>
              </select>
            )}
          </div>

          <div>
            <Label className="text-white/80 mb-1 block text-sm">
              {tplLabel('current_training_location', '¿Este mes vas a entrenar en gimnasio, casa o ambos?')}
            </Label>
            <select
              value={form.current_training_location || ''}
              onChange={(e) => setForm({...form, current_training_location: e.target.value})}
              className="w-full bg-black/40 border border-white/15 text-white rounded-md px-3 py-2 text-sm"
              data-testid="training-location-select"
            >
              <option value="">— Elige dónde entrenas —</option>
              {(tplOptions('current_training_location', [
                {value:'casa', label:'🏠 Casa'},
                {value:'gimnasio', label:'🏋️ Gimnasio'},
                {value:'ambos', label:'🔄 Ambos'},
              ])).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div>
            <label className="flex items-center gap-2 text-white/90 text-sm cursor-pointer">
              <input type="checkbox" checked={form.objective_changed} onChange={(e) => setForm({...form, objective_changed: e.target.checked})} className="w-4 h-4" />
              {tplLabel('objective_changed', '¿Tu objetivo cambió?')}
            </label>
            {form.objective_changed && (
              <select value={form.new_objective} onChange={(e) => setForm({...form, new_objective: e.target.value})}
                className="w-full bg-black/40 border border-white/15 text-white rounded-md px-3 py-2 text-sm mt-2" data-testid="new-objective">
                <option value="">— Elige tu nuevo objetivo —</option>
                {(tplOptions('objective_changed', [
                  {value:'perder_grasa', label:'🔻 Perder grasa'},
                  {value:'aumentar_masa', label:'🔺 Aumentar masa muscular'},
                  {value:'recomposicion', label:'⚖️ Recomposición corporal'},
                  {value:'mantener', label:'🎯 Mantenimiento'},
                  {value:'salud', label:'💚 Mejorar salud general'},
                ])).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}
          </div>

          <div>
            <label className="flex items-center gap-2 text-white/90 text-sm cursor-pointer">
              <input type="checkbox" checked={form.preferences_changed} onChange={(e) => setForm({...form, preferences_changed: e.target.checked})} className="w-4 h-4" />
              {tplLabel('preferences_changed', '¿Cambió alguna preferencia alimentaria?')}
            </label>
            {form.preferences_changed && (
              <Textarea value={form.preferences_notes} onChange={(e) => setForm({...form, preferences_notes: e.target.value})} placeholder="Cuéntanos qué cambió..." className="bg-black/40 border-white/15 text-white mt-2 min-h-[60px]" />
            )}
          </div>

          {/* 💊 Suplementación (2026-02): misma pregunta del formulario inicial */}
          <div data-testid="uses-supplements-section">
            <Label className="text-white/90 mb-2 block text-sm">
              {tplLabel('uses_supplements', '💊 ¿Estás usando suplementos este mes?')}
              <span className="text-white/50 font-normal"> ({tplDesc('uses_supplements', 'opcional — si no respondes, se conserva tu respuesta anterior')})</span>
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: true, l: '💊 Sí, uso suplementos' },
                { v: false, l: '🥗 No, prefiero solo comida' },
              ].map(o => (
                <button
                  key={String(o.v)}
                  type="button"
                  onClick={() => setForm({ ...form, uses_supplements: o.v })}
                  className={`p-3 rounded-lg text-sm text-left transition-all ${form.uses_supplements === o.v ? 'bg-pink-500/25 border-2 border-pink-400' : 'bg-white/5 border border-white/15 hover:border-white/30'}`}
                  data-testid={`btn-update-uses-supplements-${o.v ? 'yes' : 'no'}`}
                >
                  <p className="font-semibold text-white">{o.l}</p>
                </button>
              ))}
            </div>
            {form.uses_supplements !== null && (
              <button
                type="button"
                onClick={() => setForm({ ...form, uses_supplements: null })}
                className="mt-2 text-[11px] text-white/50 hover:text-white/80 underline"
                data-testid="btn-update-uses-supplements-clear"
              >
                Sin cambios (usar mi respuesta anterior)
              </button>
            )}
          </div>

          {/* ═════ Actualizar implementos disponibles (SIEMPRE visible) ═════ */}
          <div data-testid="equipment-update-section">
            <label className="flex items-center gap-2 text-white/90 text-sm">
              🏠 Implementos disponibles este mes
            </label>
            <div className="mt-3">
              <p className="text-xs text-white/60 mb-3">
                Marca todos los implementos que tienes ACTUALMENTE. Tu rutina se armará solo con esos.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {equipmentCatalog.map(eq => {
                  const active = (form.available_equipment || []).includes(eq.id);
                  return (
                    <button
                      key={eq.id}
                      type="button"
                      data-testid={`update-equipment-${eq.id}`}
                      onClick={() => toggleEquipment(eq.id)}
                      className={`px-3 py-2 rounded-lg border-2 text-left transition-all ${active ? 'bg-cyan-500/25 border-cyan-400' : 'bg-white/5 border-white/15 hover:border-white/30'}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{eq.icon}</span>
                        <span className={`text-xs font-semibold leading-tight ${active ? 'text-cyan-100' : 'text-white/80'}`}>
                          {eq.label}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div>
            <Label className="text-white/80 mb-1 block">{tplLabel('new_pathologies', 'Nuevas patologías o condiciones (separadas por coma)')}</Label>
            <Input value={form.new_pathologies} onChange={(e) => setForm({...form, new_pathologies: e.target.value})} placeholder={tplField('new_pathologies')?.placeholder || 'Ej: hipotiroidismo, gastritis'} className="bg-black/40 border-white/15 text-white" />
          </div>

          <div>
            <Label className="text-white/80 mb-1 block">{tplLabel('new_allergies', 'Nuevas alergias o intolerancias')}</Label>
            <Input value={form.new_allergies} onChange={(e) => setForm({...form, new_allergies: e.target.value})} placeholder={tplField('new_allergies')?.placeholder || 'Ej: lactosa, gluten'} className="bg-black/40 border-white/15 text-white" />
          </div>

          {status?.is_12week_cycle && (
            <div
              className="rounded-2xl p-4 md:p-5 space-y-4"
              style={{ background: 'rgba(52,211,153,0.08)', border: '1.5px solid rgba(52,211,153,0.35)' }}
              data-testid="fe-12week-section"
            >
              <div>
                <p className="text-[11px] uppercase tracking-widest text-emerald-300 font-bold mb-1">Ciclo nuevo · 12 semanas cumplidas</p>
                <h3 className="text-base md:text-lg font-black text-white leading-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Vamos a armar tu rutina desde cero 💪
                </h3>
                <p className="text-white/80 text-xs md:text-sm mt-1">
                  Contame cómo te sentiste este ciclo para ajustar el próximo.
                </p>
              </div>

              <div>
                <Label className="text-white/80 mb-1 block text-sm">¿Cómo te sentiste con el volumen de entrenamiento?</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { v: 'muy_alto', l: '📈 Muy alto', d: 'Difícil de sostener' },
                    { v: 'perfecto', l: '✨ Perfecto', d: 'Justo lo necesario' },
                    { v: 'muy_bajo', l: '📉 Muy bajo', d: 'Quiero más carga' },
                  ].map(o => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setForm({ ...form, training_volume_feedback: o.v })}
                      className={`text-left p-2 rounded-lg border transition-all ${form.training_volume_feedback === o.v ? 'bg-emerald-500/20 border-emerald-400' : 'bg-black/40 border-white/15 hover:border-white/30'}`}
                      data-testid={`fe-12week-volume-${o.v}`}
                    >
                      <p className="font-bold text-white text-xs">{o.l}</p>
                      <p className="text-[10px] text-white/70">{o.d}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-white/80 mb-1 block text-sm">¿Hay algún ejercicio que quieras eliminar o reemplazar?</Label>
                <Input
                  value={form.exercises_to_remove}
                  onChange={(e) => setForm({ ...form, exercises_to_remove: e.target.value })}
                  placeholder="Ej: sentadilla búlgara — la rodilla me molesta"
                  className="bg-black/40 border-white/15 text-white"
                  data-testid="fe-12week-remove"
                />
              </div>

              <div>
                <Label className="text-white/80 mb-1 block text-sm">¿Tenés equipo o material nuevo para entrenar?</Label>
                <Input
                  value={form.new_equipment_available}
                  onChange={(e) => setForm({ ...form, new_equipment_available: e.target.value })}
                  placeholder="Ej: mancuernas 8kg y banda de resistencia"
                  className="bg-black/40 border-white/15 text-white"
                  data-testid="fe-12week-equipment"
                />
              </div>

              <div>
                <Label className="text-white/80 mb-1 block text-sm">¿Dónde estás entrenando actualmente?</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { v: 'casa', l: '🏠 Casa' },
                    { v: 'gimnasio', l: '🏋️ Gimnasio' },
                    { v: 'ambos', l: '🔀 Ambos' },
                  ].map(o => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setForm({ ...form, current_training_location: o.v })}
                      className={`text-center p-2 rounded-lg border transition-all ${form.current_training_location === o.v ? 'bg-emerald-500/20 border-emerald-400' : 'bg-black/40 border-white/15 hover:border-white/30'}`}
                      data-testid={`fe-12week-location-${o.v}`}
                    >
                      <p className="font-bold text-white text-sm">{o.l}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-white/80 mb-1 block text-sm">¿Hay algún grupo muscular que quieras priorizar el próximo ciclo?</Label>
                <Input
                  value={form.muscle_group_priority}
                  onChange={(e) => setForm({ ...form, muscle_group_priority: e.target.value })}
                  placeholder="Ej: glúteos y espalda"
                  className="bg-black/40 border-white/15 text-white"
                  data-testid="fe-12week-priority"
                />
              </div>
            </div>
          )}

          <TemplateCustomFields
            formTemplate={formTemplate}
            isTwelveWeekCycle={!!status?.is_12week_cycle}
            customAnswers={customAnswers}
            onAnswerChange={setCustomAnswer}
          />

          <div>
            <Label className="text-white/80 mb-1 block">{tplLabel('notes', 'Notas para tu coach (opcional)')}</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder={tplField('notes')?.placeholder || 'Cualquier cosa que quieras contarnos...'} className="bg-black/40 border-white/15 text-white min-h-[80px]" />
          </div>

          <Button onClick={handleSubmit} disabled={submitting} className="w-full rounded-full font-bold text-white text-base py-6 hover:scale-[1.01] transition-all" style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }} data-testid="submit-update">
            {submitting ? 'Enviando...' : 'Enviar mi actualización'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ icon, label }) {
  return (
    <div className="flex items-center gap-2 pt-2">
      <span className="text-pink-400">{icon}</span>
      <p className="text-[11px] font-bold uppercase tracking-widest text-pink-300/90">{label}</p>
    </div>
  );
}

// Ticket #265569 · Renderiza las secciones/campos que el admin agregó en
// FormBuilder sobre la plantilla `fit_evolution_update`. Las preguntas ya
// hardcodeadas más arriba se excluyen (HARDCODED_FIELD_IDS) para no
// duplicarlas, y cada campo respeta su `cadence`: `12_week` solo se muestra
// cuando `isTwelveWeekCycle` es true; cualquier otro valor (o ausente, para
// plantillas viejas) se trata como `every_cycle` y se muestra siempre.
function TemplateCustomFields({ formTemplate, isTwelveWeekCycle, customAnswers, onAnswerChange }) {
  if (!formTemplate) return null;

  const visibleFieldsOf = (sectionId) => (formTemplate.fields || [])
    .filter(f => (f.section || f.section_id || null) === sectionId)
    .filter(f => !HARDCODED_FIELD_IDS.has(f.id))
    // `system_locked` marca placeholders de bloques que la UI ya renderiza
    // aparte (welcome-back, extras de 12 semanas) — no son preguntas reales.
    .filter(f => !f.system_locked)
    .filter(f => (f.cadence || 'every_cycle') !== '12_week' || isTwelveWeekCycle)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const sections = [...(formTemplate.sections || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  const sectionBlocks = [...sections.map(s => ({ ...s, fields: visibleFieldsOf(s.id) })), { id: null, title: null, fields: visibleFieldsOf(null) }]
    .filter(block => block.fields.length > 0);

  if (sectionBlocks.length === 0) return null;

  return (
    <div className="space-y-5" data-testid="template-custom-fields">
      <SectionTitle icon={<FaCamera />} label="Preguntas adicionales" />
      {sectionBlocks.map(block => (
        <div key={block.id || 'sin-seccion'} className="space-y-4">
          {block.title && (
            <p className="text-sm font-bold text-white/90">{block.icon ? `${block.icon} ` : ''}{block.title}</p>
          )}
          {block.fields.map(field => (
            <TemplateField
              key={field.id}
              field={field}
              value={customAnswers[field.id]}
              onChange={(val) => onAnswerChange(field.id, val)}
              data-testid={`template-field-${field.id}`}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
